import {
  DEFAULT_INPUT_PROFILE,
  type AuxBinding,
  type AuxMap,
  type AxisBinding,
  type AxisMap,
  type InputProfile,
} from './channels';
import type { AxisCurveConfig } from './curve';

export interface StoredInputProfile {
  version: 1;
  deviceHash: string;
  deviceName: string;
  createdAt: number;
  profile: InputProfile;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const PROFILE_KEY_PREFIX = 'similator:input-profile:';
const PROFILE_INDEX_KEY = 'similator:input-profile-index';

const curve = (deadzonePct: number, expo: number): AxisCurveConfig => ({
  min: -1,
  max: 1,
  center: 0,
  deadzonePct,
  reverse: false,
  expo,
});

const binding = (index: number, deadzonePct: number, expo: number): AxisBinding => ({
  index,
  curve: curve(deadzonePct, expo),
});

const auxMap = (): AuxMap => ({
  arm: { ...DEFAULT_INPUT_PROFILE.auxMap.arm },
  modeToggle: { ...DEFAULT_INPUT_PROFILE.auxMap.modeToggle },
  reset: { ...DEFAULT_INPUT_PROFILE.auxMap.reset },
  releaseManual: { ...DEFAULT_INPUT_PROFILE.auxMap.releaseManual },
});

const aetrProfile = (deadzonePct: number, expo: number): InputProfile => ({
  axisMap: {
    roll: binding(0, deadzonePct, expo),
    pitch: binding(1, deadzonePct, expo),
    throttle: binding(2, deadzonePct, expo),
    yaw: binding(3, deadzonePct, expo),
  },
  auxMap: auxMap(),
});

/** Built-ins use their stable deviceHash as the preset identifier. */
export const PRESETS: StoredInputProfile[] = [
  {
    version: 1,
    deviceHash: 'generic-gamepad',
    deviceName: 'Generic Gamepad',
    createdAt: 0,
    profile: aetrProfile(0, 0),
  },
  {
    version: 1,
    deviceHash: 'rc-transmitter',
    deviceName: 'RC Transmitter as Joystick',
    createdAt: 0,
    profile: aetrProfile(0.03, 0.2),
  },
];

export const presetById = (id: string): StoredInputProfile | undefined =>
  PRESETS.find((preset) => preset.deviceHash === id);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function assertFinite(value: unknown, field: string): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Invalid input profile: ${field} must be a finite number`);
  }
}

function assertIndex(value: unknown, field: string): asserts value is number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    throw new Error(`Invalid input profile: ${field} must be a non-negative integer`);
  }
}

function assertCurve(value: unknown, field: string): asserts value is AxisCurveConfig {
  if (!isRecord(value)) throw new Error(`Invalid input profile: ${field} is missing`);
  assertFinite(value.min, `${field}.min`);
  assertFinite(value.max, `${field}.max`);
  assertFinite(value.center, `${field}.center`);
  assertFinite(value.deadzonePct, `${field}.deadzonePct`);
  assertFinite(value.expo, `${field}.expo`);
  if (value.min >= value.center || value.center >= value.max) {
    throw new Error(`Invalid input profile: ${field} requires min < center < max`);
  }
  if (value.deadzonePct < 0 || value.deadzonePct > 1 || value.expo < 0 || value.expo > 1) {
    throw new Error(`Invalid input profile: ${field} deadzone and expo must be in [0, 1]`);
  }
  if (typeof value.reverse !== 'boolean') {
    throw new Error(`Invalid input profile: ${field}.reverse must be a boolean`);
  }
}

function assertAxisBinding(value: unknown, field: string): asserts value is AxisBinding {
  if (!isRecord(value)) throw new Error(`Invalid input profile: ${field} is missing`);
  assertIndex(value.index, `${field}.index`);
  assertCurve(value.curve, `${field}.curve`);
}

function assertAxisMap(value: unknown): asserts value is AxisMap {
  if (!isRecord(value)) throw new Error('Invalid input profile: profile.axisMap is missing');
  assertAxisBinding(value.roll, 'profile.axisMap.roll');
  assertAxisBinding(value.pitch, 'profile.axisMap.pitch');
  assertAxisBinding(value.yaw, 'profile.axisMap.yaw');
  assertAxisBinding(value.throttle, 'profile.axisMap.throttle');
}

function assertAuxBinding(value: unknown, field: string): asserts value is AuxBinding {
  if (!isRecord(value)) throw new Error(`Invalid input profile: ${field} is missing`);
  assertIndex(value.index, `${field}.index`);
  if (value.type !== 'button' && value.type !== 'axis') {
    throw new Error(`Invalid input profile: ${field}.type must be button or axis`);
  }
  assertFinite(value.onThreshold, `${field}.onThreshold`);
}

function assertAuxMap(value: unknown): asserts value is AuxMap {
  if (!isRecord(value)) throw new Error('Invalid input profile: profile.auxMap is missing');
  assertAuxBinding(value.arm, 'profile.auxMap.arm');
  assertAuxBinding(value.modeToggle, 'profile.auxMap.modeToggle');
  assertAuxBinding(value.reset, 'profile.auxMap.reset');
  assertAuxBinding(value.releaseManual, 'profile.auxMap.releaseManual');
}

function assertStoredProfile(value: unknown): asserts value is StoredInputProfile {
  if (!isRecord(value)) throw new Error('Invalid input profile: expected an object');
  if (value.version !== 1) throw new Error('Invalid input profile: version must be 1');
  if (typeof value.deviceHash !== 'string' || value.deviceHash.length === 0) {
    throw new Error('Invalid input profile: deviceHash is required');
  }
  if (typeof value.deviceName !== 'string' || value.deviceName.length === 0) {
    throw new Error('Invalid input profile: deviceName is required');
  }
  assertFinite(value.createdAt, 'createdAt');
  if (!isRecord(value.profile)) throw new Error('Invalid input profile: profile is missing');
  assertAxisMap(value.profile.axisMap);
  assertAuxMap(value.profile.auxMap);
}

export const exportProfile = (stored: StoredInputProfile): string => {
  assertStoredProfile(stored);
  return JSON.stringify(stored);
};

export const importProfile = (json: string): StoredInputProfile => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error('Invalid input profile JSON');
  }
  assertStoredProfile(parsed);
  return parsed;
};

const profileKey = (deviceHash: string): string => `${PROFILE_KEY_PREFIX}${deviceHash}`;

export class ProfileStore {
  constructor(private readonly storage: StorageLike) {}

  save(stored: StoredInputProfile): void {
    const json = exportProfile(stored);
    this.storage.setItem(profileKey(stored.deviceHash), json);
    const hashes = this.readIndex();
    if (!hashes.includes(stored.deviceHash)) {
      hashes.push(stored.deviceHash);
      this.storage.setItem(PROFILE_INDEX_KEY, JSON.stringify(hashes));
    }
  }

  load(deviceHash: string): StoredInputProfile | null {
    const json = this.storage.getItem(profileKey(deviceHash));
    if (json === null) return null;
    try {
      const stored = importProfile(json);
      return stored.deviceHash === deviceHash ? stored : null;
    } catch {
      return null;
    }
  }

  has(deviceHash: string): boolean {
    return this.load(deviceHash) !== null;
  }

  list(): StoredInputProfile[] {
    return this.readIndex()
      .map((deviceHash) => this.load(deviceHash))
      .filter((stored): stored is StoredInputProfile => stored !== null);
  }

  remove(deviceHash: string): void {
    this.storage.removeItem(profileKey(deviceHash));
    const hashes = this.readIndex().filter((hash) => hash !== deviceHash);
    this.storage.setItem(PROFILE_INDEX_KEY, JSON.stringify(hashes));
  }

  private readIndex(): string[] {
    const json = this.storage.getItem(PROFILE_INDEX_KEY);
    if (json === null) return [];
    try {
      const parsed: unknown = JSON.parse(json);
      return Array.isArray(parsed)
        ? parsed.filter((value): value is string => typeof value === 'string')
        : [];
    } catch {
      return [];
    }
  }
}

export type StartupProfileResolution = {
  profile: InputProfile;
  source: 'saved' | 'needs-calibration';
};

/** Ticket 27 uses this result to either continue silently or open its calibration/preset UI. */
export const resolveStartupProfile = (
  deviceHash: string,
  store: ProfileStore,
): StartupProfileResolution => {
  const saved = store.load(deviceHash);
  return saved === null
    ? { profile: DEFAULT_INPUT_PROFILE, source: 'needs-calibration' }
    : { profile: saved.profile, source: 'saved' };
};
